import { Chip } from '@liveauctioneers/hammer-ui-core/chips';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { useCallback, useMemo } from 'react';

import ShouldLogInContainer from '@/components/ShouldLogIn/ShouldLogInContainer';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { isUserLoggedIn } from '@/redux/modules/account/user/user.selectors';
import { EventContext } from '@/redux/modules/analytics';
import { CompleteAccountModalContext, ModalContext, openExcludeHousesModal } from '@/redux/modules/modal';
import { getUserExcludedHouses } from '@/redux/modules/search/exclusions/searchExclusions.selectors';
import { fetchSearchAggregationsOnDemand } from '@/redux/modules/search/search.actions';
import { QuickFilterName, useTrackQuickFilterPillClicked } from '@/utils/amplitude/analytics/searchPage';
import { useFilterDrawerReorderExperiment } from '@/utils/amplitude/experiments/hooks/useFilterDrawerReorder.experiment';

const EXCLUDE_HOUSES_MODAL_CHIP_ID = 'exclude-auction-houses-modal-chip';

export const ExcludeAuctionHousesFilter = () => {
    const dispatch = useAppDispatch();
    const isLoggedIn = useAppSelector(isUserLoggedIn);
    const hasFilterDrawerReorderTreatment = useFilterDrawerReorderExperiment();
    const trackQuickFilterPillClicked = useTrackQuickFilterPillClicked();
    const userExcludedHouses = useAppSelector((state) => getUserExcludedHouses(state));
    const hasExcludedHouses = Boolean(Array.isArray(userExcludedHouses) && userExcludedHouses.length);

    const modalContext = useMemo<ModalContext>(
        () => ({
            eventContext: EventContext.loginModal,
            id: 0,
            type: CompleteAccountModalContext.ExcludeAuctionHouses,
        }),
        []
    );

    const openExcludeHousesModalFlow = useCallback(() => {
        trackQuickFilterPillClicked(QuickFilterName.ExcludeAuctionHouses);
        dispatch(fetchSearchAggregationsOnDemand());
        dispatch(openExcludeHousesModal());
    }, [dispatch, trackQuickFilterPillClicked]);

    if (!isLoggedIn && !hasFilterDrawerReorderTreatment) {
        return null;
    }

    return (
        <ShouldLogInContainer
            modalContext={modalContext}
            runFunction={openExcludeHousesModalFlow}
        >
            <Chip
                id={EXCLUDE_HOUSES_MODAL_CHIP_ID}
                selected={hasExcludedHouses}
            >
                {!hasExcludedHouses ? (
                    <FormattedMessage id="excludeAuctionHousesFilter.chip.title" />
                ) : (
                    <FormattedMessage
                        id="excludeAuctionHousesFilter.chip.titleWithCount"
                        values={{
                            count: userExcludedHouses.length,
                        }}
                    />
                )}
            </Chip>
        </ShouldLogInContainer>
    );
};
